import * as R from '../../Router/routes';
import {
  Button,
  Card,
  ExtensionSchedule,
  Paragraph,
  Placeholder,
} from '../../components';
import { DEFAULT_DATE_FORMAT } from 'utils/translate';
import { DiscountTag } from './DiscountTag';
import { getBackUrlMenuScreens } from 'utils/getUrl';
import { getIntAndDecimal } from 'pages/ActiveLoan/utils';
import { getOverdueType } from 'utils/overdue';
import { getToday } from 'utils/shiftedDateTime';
import { Helmet } from 'react-helmet-async';
import { isDiscountActive } from 'utils/discounts';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { urlBundlePreloadHashMap } from '../../Router/Router';
import { useAnalytics } from 'use-analytics';
import { useBusinessOverdue } from '../../hooks/businessLogic/useBusinessOverdue';
import {
  useClient,
  useClientSettings,
  useDiscounts,
  useLatestLoan,
  usePaymentSchedule,
} from '../../hooks/webapi';
import { useLocation, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import CalendarGreenIcon from 'assets/icons/calendar-green.svg?react';
import classes from 'classnames';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import dayjs from 'dayjs';
import LightbulbErrorIcon from 'assets/icons/lightbulb-red.svg?react';
import LightbulbGreenIcon from 'assets/icons/lightbulb-green.svg?react';
import styles from './ExtensionsOverdue.module.scss';
import Tracked from '../../customAnalytics/components/Tracked';
import type { LoanExtension } from '../../hooks/webapi/useLatestLoan';

const INITIAL_VALUES_LOAN_EXTENSIONS_FEES: Array<LoanExtension> = [
  {
    term: 30,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
  {
    term: 14,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
  {
    term: 7,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
];

// @TODO: refactor the whole screen
export default memo(function Extensions() {
  const { t } = useTranslation('extensions');
  const { client } = useClient();
  const { latestLoan, selectedLoanExtension, setSelectedLoanExtension } =
    useLatestLoan();
  const { discounts, fetchDiscounts, largestExtensionDiscount } =
    useDiscounts();
  const { paymentSchedule } = usePaymentSchedule();
  const { penaltiesAndCommissions, amount } = useBusinessOverdue();
  const [loading, setLoading] = useState(true);
  const { clientSettings } = useClientSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();

  const isProductNumber6 = latestLoan?.productNumber === '6';
  const isProductNumber8 = latestLoan?.productNumber === '8';

  // Apply PID on url in case user has arrived to here externally without the product number
  useEffect(() => {
    const pidNotDefined =
      !location.search.includes('PID') ||
      !!location.search.includes('PID=undefined');
    if (pidNotDefined && latestLoan?.productNumber) {
      let queryParams = location.search;
      // ADD PID to the url
      if (!!location.search.includes('PID=undefined')) {
        queryParams = queryParams.replace(
          'PID=undefined',
          `PID=${latestLoan?.productNumber}`,
        );
      } else {
        const symbol = location.search.includes('?') ? '&' : '?';
        queryParams += `${symbol}PID=${latestLoan?.productNumber}`;
      }
      navigate(`${location.pathname}${queryParams}`);
    }
  }, [location.pathname, location.search, latestLoan?.productNumber, navigate]);

  useEffect(() => {
    (async () => {
      try {
        urlBundlePreloadHashMap[(R.EXTENSION_PAYMENT, R.NEWS_CONSENT)]();

        await Promise.all([!discounts && fetchDiscounts()]);

        setLoading(false);

        if (!latestLoan) {
          navigate(R.REPEATED_LOAN, { replace: true });

          return;
        }

        if (fullExtensionFees && fullExtensionFees.length < 1) {
          navigate(R.ACTIVE_LOAN, { replace: true });
          return;
        }
      } catch {}
    })();
  }, [fetchDiscounts, latestLoan, navigate]);

  const handleSubmit = useCallback(() => {
    !Boolean(clientSettings?.acceptNews)
      ? navigate(R.NEWS_CONSENT, {
          state: { nextUrl: R.EXTENSION_PAYMENT, selectedLoanExtension },
        })
      : navigate(R.EXTENSION_PAYMENT);
  }, [selectedLoanExtension, clientSettings?.acceptNews, navigate]);

  const getExtensionCurrentTerm = useCallback(
    (dueDate: string) => {
      return (isProductNumber6 || isProductNumber8) &&
        getToday() > new Date(dueDate)
        ? getToday()
        : dueDate;
    },
    [latestLoan],
  );

  const handleAnalytics = useCallback(
    (termString: string) => {
      const eventType = 'change';
      const data = {
        location: location.pathname,
        value: termString,
        description: 'Extenstion term selected',
        stateKey: 'extensions.termSelectedTs',
        valueKey: 'extensions.termSelectedValue',
      };

      track(eventType, data);
    },
    [location.pathname, track],
  );

  const getExtensionDate = ({ term, unit }: { term: number; unit: string }) => {
    if (!latestLoan?.dueDate) return '';

    const extensionDate = getExtensionCurrentTerm(latestLoan?.dueDate);

    const extensionDateWithOverdue = dayjs(extensionDate).add(
      term,
      unit === 'DAYS' ? 'day' : 'month',
    );

    const day = dayjs(extensionDateWithOverdue).format('D');
    const monthName = dayjs(extensionDateWithOverdue)
      .format('MMMM')
      .toUpperCase();

    const year = dayjs(extensionDateWithOverdue).format('YYYY');

    return `${day} ${monthName} ${year}`;
  };

  useEffect(() => {
    if (
      latestLoan?.productCode === 'CONS' &&
      latestLoan?.isExtensionPossible &&
      !selectedLoanExtension
    ) {
      setSelectedLoanExtension(latestLoan?.loanExtensionFees[0]);
    }
  }, [latestLoan?.loanExtensionFees, latestLoan?.productCode]);

  const isMini = latestLoan?.productCode === 'MINI';
  const isMini8 = isMini && isProductNumber8;

  const isActiveDiscountTag =
    !isMini ||
    (isMini &&
      isDiscountActive({
        amount: latestLoan?.amount,
        availableDiscounts: discounts,
      }));

  const getFullExtensionFees = useCallback(() => {
    const extensions =
      latestLoan?.loanExtensionFees || INITIAL_VALUES_LOAN_EXTENSIONS_FEES;
    return extensions.sort(({ term: termA }, { term: termB }) => termB - termA);
  }, [latestLoan?.loanExtensionFees]);

  const fullExtensionFees = getFullExtensionFees();

  const renderExtensionCard = useCallback(() => {
    return fullExtensionFees.map(
      ({ term, unit, fee, feeBeforeDiscount }, index) => {
        const extension = fullExtensionFees[index];
        const isExtensionSelected =
          selectedLoanExtension?.term === extension?.term;
        return (
          <>
            {index >= 2 && index === fullExtensionFees.length - 1 && (
              <div
                className={classes(styles.subtitle, styles['term-separator'])}
                data-test="extensionsOverdue-term-separator-mobile"
              >
                {t('termSeparatorMobile')}
              </div>
            )}
            <Card
              key={`${term}-${index}`}
              className={classes(
                styles['extension-card'],
                isExtensionSelected && styles['extension-card-selected'],
              )}
              onClick={() => {
                if (extension) handleAnalytics(extension.term.toString());
                setSelectedLoanExtension(extension);
              }}
              dataTest={`loan-extension-${term}`}
            >
              <div className={styles['card-content']}>
                <div className={styles['card-label-days']}>
                  <Placeholder
                    loading={loading}
                    className={styles['card-label-days']}
                    dataTest="extensionsOverdue-days"
                  >
                    <span
                      className={styles.term}
                      data-test="extensionsOverdue-days-term"
                    >
                      {term}
                    </span>
                    <span
                      className={styles.unit}
                      data-test="extensionsOverdue-days-unit"
                    >
                      {unit === 'DAYS'
                        ? t('common:days', { count: term || 0 })
                        : t('common:months', { count: term || 0 })}
                    </span>
                  </Placeholder>
                </div>
                <Paragraph
                  type="small"
                  className={styles['card-content-out-term']}
                >
                  <CalendarGreenIcon
                    className={styles['calendar-icon-desktop']}
                    data-test="extensionsOverdue-calendar-icon"
                  />
                  {latestLoan && (
                    <Placeholder
                      className={styles['extension-date-wrapper']}
                      loading={loading}
                      dataTest="extensionsOverdue-date"
                    >
                      <span
                        className={styles['extension-date-label']}
                        data-test="extensionsOverdue-date-label"
                      >
                        {t('date')}
                      </span>
                      <div
                        className={styles['calendar-extension-date-wrapper']}
                      >
                        <CalendarGreenIcon
                          className={styles['calendar-icon-mobile']}
                          data-test="extensionsOverdue-date-calendar"
                        />
                        <span
                          className={styles['extension-date']}
                          data-test="extensionsOverdue-extension-date"
                        >
                          {getExtensionDate({ term, unit })}
                        </span>
                      </div>
                    </Placeholder>
                  )}
                  <Button
                    dataTest={`extension-btn-${extension?.term}`}
                    size="m"
                    onClick={() => setSelectedLoanExtension(extension)}
                    outline={!isExtensionSelected}
                    className={styles['extension-btn']}
                    buttonClassName={styles['extension-btn']}
                  >
                    {isExtensionSelected
                      ? t('extensionSelectedBtn')
                      : t('extensionBtn')}
                  </Button>
                </Paragraph>
              </div>
            </Card>
          </>
        );
      },
    );
  }, [isMini, latestLoan, loading, selectedLoanExtension, fullExtensionFees]);

  const extensionPaymentSchedule = useMemo(() => {
    if (latestLoan?.productCode !== 'CONS' || !latestLoan?.isExtensionPossible)
      return undefined;

    return paymentSchedule?.items
      .filter(({ status }) => status === undefined || status === 'DELAYED')
      .map(({ dueDate }) => ({
        currentDate: dayjs(getExtensionCurrentTerm(dueDate)).format(
          DEFAULT_DATE_FORMAT,
        ),
        newDate: dayjs(getExtensionCurrentTerm(dueDate))
          .add(
            fullExtensionFees[0]?.term,
            fullExtensionFees[0]?.unit === 'DAYS' ? 'day' : 'month',
          )
          .format(DEFAULT_DATE_FORMAT),
      }));
  }, [
    latestLoan?.isExtensionPossible,
    fullExtensionFees,
    latestLoan?.productCode,
    paymentSchedule?.items,
  ]);

  const feeBeforeDiscount =
    largestExtensionDiscount?.discountPercent && fullExtensionFees
      ? fullExtensionFees[0].fee /
        ((100 - largestExtensionDiscount?.discountPercent) / 100)
      : undefined;

  const showOverdueNotif =
    Boolean(latestLoan?.overDueDays) && (isProductNumber6 || isMini8);

  const overdueType = getOverdueType(latestLoan);

  const penaltyGracePeriod =
    penaltiesAndCommissions && getIntAndDecimal(penaltiesAndCommissions, '.');

  return (
    <Dashboard
      small
      backUrl={getBackUrlMenuScreens(client, latestLoan)}
      className={styles['body-wrapper']}
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className={styles.heading} data-test="extensionsOverdue-heading">
        {t('heading')}
      </div>
      {showOverdueNotif && overdueType === 'error' && (
        <div
          className={classes(
            styles['overdue-box'],
            styles[`overdue-box-${overdueType}`],
          )}
        >
          <LightbulbErrorIcon
            className={styles['overdue-icon']}
            width="36px"
            data-test="extensionsOverdue-lightbulb-icon"
          />

          {isMini8 && (
            <span
              className={styles['no-wrap-container']}
              data-test="extensionsOverdue-mini8-extension"
              dangerouslySetInnerHTML={{
                __html:
                  latestLoan?.overDueDays && latestLoan.overDueDays <= 25
                    ? t('overdueTo25_8', {
                        penalties:
                          (latestLoan?.penaltyAmount &&
                            getIntAndDecimal(latestLoan?.penaltyAmount)) ||
                          '0',
                        fee: amount && getIntAndDecimal(amount),
                      })
                    : t('overdueFrom26_8', {
                        penalties:
                          (latestLoan?.penaltyAmount &&
                            getIntAndDecimal(latestLoan?.penaltyAmount)) ||
                          '0',
                        fee: amount && getIntAndDecimal(amount),
                      }),
              }}
            ></span>
          )}
          <span data-test="extensionsOverdue-error-notif-text">
            {isProductNumber6 &&
              t('overdue', {
                fee: amount && getIntAndDecimal(amount),
              })}
          </span>
        </div>
      )}
      {showOverdueNotif && overdueType === 'warning' && (
        <div
          className={classes(
            styles['overdue-box'],
            styles[`overdue-box-${overdueType}`],
          )}
        >
          <LightbulbGreenIcon
            className={styles['overdue-icon']}
            data-test="extensionsOverdue-warning-notif-lightbulb"
            width="24px"
          />
          <span
            className={styles['no-wrap-container']}
            data-test="extensionsOverdue-warning-notif-text"
            dangerouslySetInnerHTML={{
              __html: t('overdueWarning', {
                penalties:
                  penaltyGracePeriod &&
                  getIntAndDecimal(parseFloat(penaltyGracePeriod)),
                fee: amount && getIntAndDecimal(amount),
              }),
            }}
          ></span>
        </div>
      )}
      <Paragraph
        className={styles.subtitle}
        dataTest="extensionsOverdue-extension-subtitle-7_14"
      >
        {t('subtitle7_14')}
      </Paragraph>
      {!!largestExtensionDiscount && isActiveDiscountTag && (
        <div className={styles.discount}>
          <DiscountTag
            discountPercent={largestExtensionDiscount.discountPercent}
            testIdPrefix="extensionsOverdue"
          />
        </div>
      )}
      {latestLoan?.isExtensionPossible && (
        <div className={styles['extensions-wrapper']}>
          <div className={styles.extensions}>
            {latestLoan?.productCode === 'CONS' ? (
              <div className={styles['extensions-cons']}>
                <ExtensionSchedule
                  term={fullExtensionFees[0].term}
                  unit={fullExtensionFees[0].unit}
                  fee={fullExtensionFees[0].fee}
                  feeBeforeDiscount={feeBeforeDiscount}
                  schedule={extensionPaymentSchedule}
                />
              </div>
            ) : (
              renderExtensionCard()
            )}
          </div>
          <Tracked description="Continue button clicked">
            <Button
              className={styles.continue}
              disabled={!selectedLoanExtension}
              dataTest="continue"
              onClick={handleSubmit}
            >
              {t('common:continue')}
            </Button>
          </Tracked>
        </div>
      )}
    </Dashboard>
  );
});
